import { useEffect, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Button } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import { pruneLocalCopies, sidecars, type SessionSidecar } from './browser';
import { CALENDAR_PATH } from './constants';
import { QM_COPY, STATUS_COPY } from './copy';
import { failureReason, isSlowProcessing } from './format';
import type { QuickMeetingContextValue } from './QuickMeetingContext';
import type { RecordingSummary } from './types';

const DOT_TONE: Partial<Record<RecordingSummary['status'], string>> = {
  ready: 'bg-primary',
  failed: 'bg-destructive',
  no_speech: 'bg-muted-foreground',
  upload_failed: 'bg-warning',
  stale: 'bg-warning',
  salvaged_pending_review: 'bg-warning',
  recording: 'bg-warning',
};

function RowShell({
  title,
  when,
  status,
  tone,
  busy,
  children,
}: {
  title: string;
  when: string | null;
  status: string;
  tone?: string;
  busy?: boolean;
  children?: ReactNode;
}) {
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        {busy ? (
          <Loader2 className="text-muted-foreground h-3 w-3 shrink-0 animate-spin" aria-hidden />
        ) : (
          <span className={cn('h-2 w-2 shrink-0 rounded-full', tone ?? 'bg-muted-foreground')} aria-hidden />
        )}
        <span title={title} className="text-foreground min-w-0 flex-1 truncate text-lg font-medium">
          {title}
        </span>
        {when && <span className="text-muted-foreground shrink-0 text-sm">{when}</span>}
      </div>
      <p className="text-md text-muted-foreground pl-4">{status}</p>
      {children && <div className="flex flex-wrap gap-1 pl-3">{children}</div>}
    </li>
  );
}

function rowAction(pendingId: string | null, label: string, onClick: () => void, id: string, tone?: 'danger') {
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className={cn(tone === 'danger' && 'text-destructive-fg')}
      disabled={pendingId === id}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

const startedLabel = (r: RecordingSummary) => format(new Date(r.started_at), 'd MMM, h:mm a');

interface RecordingsStatusListProps {
  qm: QuickMeetingContextValue;
  /** A live clock for the "taking longer" label. */
  now: number;
  /** Discard asks first; the dialog lives outside the popover, which closes on outside clicks. */
  onDiscard: (id: string) => void;
}

/** The caller's unsaved, in-flight and unseen recordings, with the action each one needs. */
export function RecordingsStatusList({ qm, now, onDiscard }: RecordingsStatusListProps) {
  const { status, local, remote, rows, access } = qm;
  const open = (path: string) => qm.openCalendarPath(path);

  const ownId = local.recordingId;
  const recoverable = (status?.recoverable ?? []).filter(
    (r) => r.id !== ownId && r.id !== remote?.recordingId,
  );
  const processing = status?.processing ?? [];
  const unseen = status?.unseen ?? [];
  if (!recoverable.length && !processing.length && !unseen.length) return null;

  const action = (label: string, onClick: () => void, id: string, tone?: 'danger') =>
    rowAction(rows.pendingId, label, onClick, id, tone);

  return (
    <section aria-label={QM_COPY.yourRecordings} className="border-border border-t">
      <h3 className="text-muted-foreground px-3 pb-1 pt-2 text-sm font-medium uppercase tracking-wide">
        {QM_COPY.yourRecordings}
      </h3>
      <ul className="divide-border divide-y">
        {recoverable.map((r) => (
          <RowShell
            key={r.id}
            title={r.title}
            when={startedLabel(r)}
            status={STATUS_COPY[r.status] ?? r.status}
            tone={DOT_TONE[r.status]}
            busy={rows.pendingId === r.id}
          >
            {r.status === 'salvaged_pending_review'
              ? access.canRecord && action(QM_COPY.process, () => void rows.process(r.id), r.id)
              : access.canRecord && action(QM_COPY.save, () => void rows.save(r.id, r.recorded_seconds), r.id)}
            {action(QM_COPY.discard, () => onDiscard(r.id), r.id, 'danger')}
          </RowShell>
        ))}
        {processing.map((r) => (
          <RowShell
            key={r.id}
            title={r.title}
            when={startedLabel(r)}
            status={isSlowProcessing(r, now) ? QM_COPY.slowProcessing : (STATUS_COPY[r.status] ?? r.status)}
            busy
          >
            {action(QM_COPY.open, () => open(CALENDAR_PATH.recording(r.id)), r.id)}
          </RowShell>
        ))}
        {unseen.map((r) => (
          <RowShell
            key={r.id}
            title={r.title}
            when={startedLabel(r)}
            status={
              r.status === 'failed'
                ? failureReason(r)
                : r.status === 'no_speech' && r.duration_seconds === 0
                  ? QM_COPY.noAudio
                  : (STATUS_COPY[r.status] ?? r.status)
            }
            tone={DOT_TONE[r.status]}
            busy={rows.pendingId === r.id}
          >
            {action(QM_COPY.openRecording, () => open(CALENDAR_PATH.recording(r.id)), r.id)}
            {r.status === 'failed' &&
              r.failure?.retryable === true &&
              access.canRecord &&
              action(QM_COPY.retryProcessing, () => void rows.retryProcessing(r.id), r.id)}
            {r.status === 'failed' &&
              action(QM_COPY.writeMomManually, () => open(CALENDAR_PATH.event(r.event_id, r.started_at)), r.id)}
          </RowShell>
        ))}
      </ul>
    </section>
  );
}

interface OrphanedRecordingRowProps {
  qm: QuickMeetingContextValue;
  recording: RecordingSummary;
  onDiscard: (id: string) => void;
}

/** Stands in for the start form while a take nothing drives is still `recording` on the server: it reads as paused. */
export function OrphanedRecordingRow({ qm, recording: r, onDiscard }: OrphanedRecordingRowProps) {
  const { rows, access } = qm;
  return (
    <ul className="-mx-3">
      <RowShell
        title={r.title}
        when={startedLabel(r)}
        status={QM_COPY.pausedRow}
        tone={DOT_TONE[r.status]}
        busy={rows.pendingId === r.id}
      >
        {/* Any device can Save: without local parts the server completes from what reached S3. */}
        {access.canRecord && rowAction(rows.pendingId, QM_COPY.save, () => void rows.save(r.id, r.recorded_seconds), r.id)}
        {rowAction(rows.pendingId, QM_COPY.discard, () => onDiscard(r.id), r.id, 'danger')}
      </RowShell>
    </ul>
  );
}

interface LocalCopiesListProps {
  qm: QuickMeetingContextValue;
  /** Delete asks first; the dialog lives outside the popover, like Discard's. */
  onDelete: (entry: SessionSidecar) => void;
}

/** Takes that ended with their audio still on this device: the safety net to download it. */
export function LocalCopiesList({ qm, onDelete }: LocalCopiesListProps) {
  const { scope, rows } = qm;
  const [, setPruned] = useState(0);
  useEffect(() => {
    if (!scope) return;
    let live = true;
    void pruneLocalCopies(scope).then(() => {
      if (live) setPruned((n) => n + 1);
    });
    return () => {
      live = false;
    };
  }, [scope]);
  const entries = scope ? sidecars.list(scope).filter((s) => s.ended) : [];
  if (!entries.length) return null;

  return (
    <section aria-label={QM_COPY.onThisDevice} className="border-border border-t">
      <h3 className="text-muted-foreground px-3 pb-1 pt-2 text-sm font-medium uppercase tracking-wide">
        {QM_COPY.onThisDevice}
      </h3>
      <ul className="divide-border divide-y">
        {entries.map((e) => (
          <RowShell
            key={e.recordingId}
            title={e.title || QM_COPY.titlePlaceholder}
            when={e.startedAt ? format(new Date(e.startedAt), 'd MMM, h:mm a') : null}
            status={QM_COPY.keptOnDevice}
            tone="bg-warning"
            busy={rows.pendingId === e.recordingId}
          >
            {rowAction(rows.pendingId, QM_COPY.download, () => void rows.downloadLocal(e), e.recordingId)}
            {rowAction(rows.pendingId, QM_COPY.deleteFromDevice, () => onDelete(e), e.recordingId, 'danger')}
          </RowShell>
        ))}
      </ul>
    </section>
  );
}
